import { DateTime } from 'luxon';
import {
  CREATE_BOARD_CARD_TEMPLATE,
  GET_BOARD_CARD_TEMPLATES,
  DELETE_BOARD_CARD_TEMPLATE,
  UPDATE_BOARD_CARD_TEMPLATE,
  GET_BOARDS,
  GET_BOARDS_FOR_DROPDOWN,
  CREATE_BOARD,
  GET_BOARD_DETAILS,
  GET_BOARD_PERMISSIONS,
  GET_BOARD_CARDS_FOR_DROPDOWN,
  FLUSH_BOARD_CARDS_FOR_DROPDOWN,
  GET_CARD_BY_ID,
  CREATE_CARD,
  UPDATE_CARD,
  MOVE_CARD,
  GET_BOARD_CARD_FILES,
  DELETE_CARD,
  TOGGLE_BOARD_LOCK,
  GET_BOARD_CARD_NOTES,
  ADD_BOARD_CARD_NOTE,
  DELETE_BOARD,
  UPDATE_BOARD,
  GET_CARD_LINKS,
  GET_CARDS_BY_LINK,
  GET_MY_CARD_TAGS,
  MARK_CARD_TAGS_AS_READ,
  NEW_TAGS_FROM_SOCKET,
  GET_CARD_EVENTS,
  GET_CARD_ATTACHMENTS,
  REORDER_CARDS,
  SWITCH_BOARD_VIEW_TYPE,
  OPEN_CARD_DRAWER,
  CLOSE_CARD_DRAWER,
  OPEN_FORM_MAP_DRAWER,
  CLOSE_FORM_MAP_DRAWER,
  OPEN_FORM_DRAWER,
  CLOSE_FORM_DRAWER,
  GET_BOARDS_LIBRARY,
  GET_BOARDS_LIBRARY_TEMPLATE,
  IMPORT_FROM_BOARD_LIBRARY,
  FIX_BOARD_INDICES,
  GET_CARD_EMAILS,
  GET_CARD_EMAIL_THREAD,
  GET_CARD_EMAIL_FILES,
  SET_BOARD_TARGET_CARD,
  SET_BOARD_FILTERS,
  UPDATE_BOARD_FILTERS,
  GET_BOARD_FILTER_VIEWS,
  CREATE_BOARD_FILTER_VIEW,
  UPDATE_BOARD_FILTER_VIEW,
  DELETE_BOARD_FILTER_VIEW,
  FAVOURITE_BOARD_FILTER_VIEW,
  CREATE_BOARD_FORM_MAPPING,
  UPDATE_BOARD_FORM_MAPPING,
  DELETE_BOARD_FORM_MAPPING,
  GET_BOARD_FORM_MAPPINGS,
  GET_BOARD_TEMPLATES,
  CREATE_BOARD_TEMPLATE,
  DELETE_BOARD_TEMPLATE,
  UPDATE_BOARD_TEMPLATE,
  MASS_UPLOAD_CARDS,
  CLEAR_SELECTED_CARD,
  SWITCH_BOARD_GANTT_VIEW_TYPE,
  SWITCH_BOARD_GANTT_ZOOM_SCALE,
  COLLAPSE_ALL_BOARD_SECTIONS,
  EXPAND_ALL_BOARD_SECTIONS,
  GET_CARD_SNAPSHOTS,
  GET_CARD_SNAPSHOT_BY_ID,
  CLEAR_SNAPSHOT_CARD_DATA,
  SET_SNAPSHOT_CARD_DATA,
  GET_ALL_BOARDS_DETAILS,
  CLEAR_CARD_SHADOW,
  SET_CARD_DRAGGING,
  GET_BOARD_STATUSES,
  GET_BOARD_COLORS,
  SET_FILTERED_CARD_IDS,
  GET_SHORT_LIST,
  GET_SHORT_LISTS,
  CREATE_SHORT_LIST,
  UPDATE_SHORT_LIST,
  DELETE_SHORT_LIST,
  CREATE_SHORT_LIST_CATEGORY,
  UPDATE_SHORT_LIST_CATEGORY,
  DELETE_SHORT_LIST_CATEGORY,
  CREATE_SHORT_LIST_ITEM,
  UPDATE_SHORT_LIST_ITEM,
  DELETE_SHORT_LIST_ITEM,
  ADD_SHORT_LIST_PERMISSION,
  UPDATE_SHORT_LIST_PERMISSION,
  DELETE_SHORT_LIST_PERMISSION,
  ADD_SHORT_LIST_CUSTOM_ITEM,
  REORDER_SHORT_LIST_CATEGORIES,
  REORDER_SHORT_LIST_ITEMS,
  OPEN_SHORT_LIST_DRAWER,
  CLOSE_SHORT_LIST_DRAWER,
  TOGGLE_CARD_WORK_COMPLETION,
  DELETE_USER_FROM_CARD_WORK_COMPLETION,
} from '../../state/actionTypes';

import { removeFromObject, updateArray, updateObject } from '../../helpers/stateHelpers';
import { getAndSetLRUReducer, getIdMap } from '../../helpers/helpers';

import Permissions from '../../auth/Permissions';
import { insertCardIntoCorrectPosition } from '../boardHelpers';
import { getStoredBoardCardSort } from '../boards.helpers';

const MAX_KEYS_ALLOWED = 10;

// Returns a new boardCardsForDropdown map with the given boardIds removed.
// Returns the original reference when there is nothing to remove so reducer
// cases that always invoke this don't allocate when no flush is needed.
const flushDropdownBoards = (boardCardsForDropdown = {}, boardIds = []) => {
  const idsToRemove = boardIds.filter((boardId) => (
    !!boardId && boardId in boardCardsForDropdown
  ));
  if (!idsToRemove.length) return boardCardsForDropdown;
  const next = { ...boardCardsForDropdown };
  idsToRemove.forEach((boardId) => {
    delete next[boardId];
  });
  return next;
};

const initialState = {
  cardTemplates: {},
  boards: {},
  boardsForDropdown: [],
  cardFileMap: {},
  cardNotesMap: {},
  cardLinks: [],
  cardUserMap: {},
  myCardTags: [],
  myReadCardTags: [],
  tagsPerCard: {},
  cardEvents: {},
  sortType: { type: 'default' },
  viewType: window.localStorage.getItem('boardViewType') ?? 'board',
  ganttViewType: window.localStorage.getItem('boardGanttViewType') ?? 'Month',
  ganttZoomScale: window.localStorage.getItem('boardGanttZoomScale') ?? 1,
  selectedColumns: ['name', 'start', 'end'],
  collapseAllTrigger: null,
  drawerConfig: {},
  formDrawerConfig: {},
  formMapDrawerConfig: {},
  formMappings: [],
  library: [],
  libraryTemplates: {},
  cardEmailMap: {},
  cardEmailThreads: {},
  cardEmailFiles: {},
  targetCard: {}, // Used to scroll to the specific card,
  filters: null,
  filterViews: [],
  boardTemplates: [],
  cardAttachments: {
    instantiatedFiles: [],
    locationMap: {},
  },
  notesFileMap: {},
  boardDetailsMap: {},
  boardPermissionsUsers: [],
  /*
    Lightweight card data for dropdowns
    { boardId: { cards: [{ id, name, statusTitle, lastUpdated }] } }
  */
  boardCardsForDropdown: {},
  cardSnapshotMap: {},
  filteredSnapshotData: null,
  isFiltered: false,
  isDragging: false,
  fullCards: {}, // Map of id to card
  cardIds: [], // list of ids
  statusToCardIdMap: {}, // { [statusId]: [cardId1, cardId2]}
  statusToShadowIdMap: {}, // { [statusId]: [cardId1, cardId2]}
  boardStatuses: [],
  boardColors: [],
  filteredCardIds: {},
  shortListCategoryMap: {},
  shortListItemMap: {},
  shortListPermissionMap: {},
  shortLists: [],
  shortListCategories: [],
  shortListDrawerConfig: {},
};

const createTagCountMap = (tags = []) => {
  const tagPerCardMap = {};
  tags.forEach((tag) => {
    const { cardId } = tag;
    const {
      [cardId]: oldCount = 0,
    } = tagPerCardMap;
    tagPerCardMap[cardId] = oldCount + 1;
  });
  return tagPerCardMap;
};

/* eslint-disable no-param-reassign */
const updateCardState = (newState = {}, cards = []) => {
  newState.statusToCardIdMap = {};
  newState.statusToShadowIdMap = {};
  newState.cardIds = [];
  newState.fullCards = {};

  cards.forEach((card) => {
    const ourMap = card.isShadow
      ? newState.statusToShadowIdMap
      : newState.statusToCardIdMap;
    if (!(card.statusId in ourMap)) {
      ourMap[card.statusId] = [];
    }
    ourMap[card.statusId].push(card.id);
    newState.cardIds.push(card.id);
    newState.fullCards[card.id] = card;
  });
};
/* eslint-enable no-param-reassign */

export default (state = initialState, action = {}) => {
  switch (action.type) {
    case GET_BOARDS_LIBRARY: {
      const {
        payload: {
          library: newLibrary = [],
        } = {},
      } = action;
      return {
        ...state,
        library: newLibrary,
      };
    }
    case GET_BOARDS_LIBRARY_TEMPLATE: {
      const {
        payload: {
          key,
          templateData: { template = {} } = {},
        } = {},
      } = action;
      const { libraryTemplates: oldLibTemplates = {} } = state;
      const newLibTemplates = { ...oldLibTemplates };
      newLibTemplates[key] = template;
      return {
        ...state,
        libraryTemplates: newLibTemplates,
      };
    }
    case GET_BOARD_CARD_TEMPLATES: {
      const {
        payload: {
          boardCardTemplates: newTemplates = {},
        } = {},
      } = action;
      return {
        ...state,
        cardTemplates: newTemplates,
      };
    }
    case CREATE_BOARD_CARD_TEMPLATE: {
      const {
        payload: {
          newCardTemplate = {},
        } = {},
      } = action;
      const newTemplates = { ...state.cardTemplates };
      newTemplates[newCardTemplate.id] = newCardTemplate;
      return {
        ...state,
        cardTemplates: newTemplates,
      };
    }
    case DELETE_BOARD_CARD_TEMPLATE: {
      const {
        payload: {
          cardTemplateId = {},
        } = {},
      } = action;
      return {
        ...state,
        cardTemplates: removeFromObject({ oldObject: state.cardTemplates, id: cardTemplateId }),
      };
    }
    case UPDATE_BOARD_CARD_TEMPLATE: {
      const {
        payload: {
          id,
          newData = {},
        } = {},
      } = action;
      return {
        ...state,
        cardTemplates: updateObject({ oldObject: state.cardTemplates, id, newData }),
      };
    }
    case GET_BOARDS: {
      const {
        payload: {
          boards: newBoards = {},
        } = {},
      } = action;
      return {
        ...state,
        boards: getIdMap(newBoards),
      };
    }
    case GET_BOARDS_FOR_DROPDOWN: {
      const {
        payload: {
          boards: newBoards = [],
        } = {},
      } = action;
      return {
        ...state,
        boardsForDropdown: newBoards,
      };
    }
    case IMPORT_FROM_BOARD_LIBRARY: {
      const {
        payload: {
          newBoard = {},
          card: newCardTemplate,
        } = {},
      } = action;
      const newBoards = { ...state.boards };
      const newTemplates = { ...state.cardTemplates };

      if (newCardTemplate) {
        newTemplates[newCardTemplate.id] = newCardTemplate;
      }
      newBoards[newBoard.id] = newBoard;
      return {
        ...state,
        boards: newBoards,
        cardTemplates: newTemplates,
      };
    }
    case CREATE_BOARD: {
      const {
        payload: {
          newBoard = {},
        } = {},
      } = action;
      const newBoards = { ...state.boards };
      newBoards[newBoard.id] = newBoard;
      return {
        ...state,
        boards: newBoards,
      };
    }
    case UPDATE_BOARD: {
      const {
        payload: {
          id,
          newData = {},
          newStatuses = [],
          newColors,
          updatedCards,
        } = {},
      } = action;

      const updatePayload = { ...newData };
      delete updatePayload.addedUsers;
      delete updatePayload.removedUsers;

      const updatedState = {
        ...state,
        boards: updateObject({ oldObject: state.boards, id, newData: updatePayload }),
      };

      const {
        selectedBoard = {},
      } = state;

      if (selectedBoard?.id !== id) return updatedState;

      const newSelectedBoard = { ...selectedBoard };
      const {
        workflows = [],
        users = [],
        colors = [],
      } = newData;
      newSelectedBoard.users = users;
      newSelectedBoard.statuses = newStatuses;
      newSelectedBoard.workflows = workflows;
      if (newColors) {
        newSelectedBoard.colors = newColors;
      } else if (colors.length) {
        newSelectedBoard.colors = colors;
      }

      if (updatedCards) {
        updateCardState(updatedState, updatedCards);
      }

      updatedState.selectedBoard = newSelectedBoard;

      return updatedState;
    }
    case DELETE_BOARD: {
      const {
        payload: {
          id,
        } = {},
      } = action;
      const newBoards = { ...state.boards };
      delete newBoards[id];
      return {
        ...state,
        boards: newBoards,
      };
    }
    case TOGGLE_BOARD_LOCK: {
      const {
        payload: {
          id,
          isLocked,
        } = {},
      } = action;
      if (!(id in state.boards)) return state;
      const newBoards = { ...state.boards };
      const {
        [id]: existingBoard = {},
      } = newBoards;
      newBoards[id] = {
        ...existingBoard,
        isLocked,
      };
      return {
        ...state,
        boards: newBoards,
      };
    }
    case GET_BOARD_PERMISSIONS: {
      const {
        payload: {
          users = [],
        } = {},
      } = action;

      return {
        ...state,
        boardPermissionsUsers: users,
      };
    }
    case GET_BOARD_DETAILS: {
      const {
        payload: {
          boardId,
          boardDetails = {},
          shouldUpdateSelectedBoard = true,
        } = {},
      } = action;

      const {
        cards,
        favouriteFilterCardIds,
        ...otherDetails
      } = boardDetails;

      const { boardDetailsMap: oldBoardDetailsMap = {} } = state;

      const newState = {
        ...state,
        boardDetailsMap: {
          ...oldBoardDetailsMap,
          [boardId]: boardDetails,
        },
      };

      if (shouldUpdateSelectedBoard) {
        newState.selectedBoard = otherDetails;
        newState.filters = null;
        newState.sortType = getStoredBoardCardSort(boardId);
        updateCardState(newState, cards);
        const nextMap = { ...state.filteredCardIds };
        nextMap[boardId] = favouriteFilterCardIds;
        newState.filteredCardIds = nextMap;
      }

      return newState;
    }
    case GET_BOARD_CARDS_FOR_DROPDOWN: {
      const {
        payload: {
          cardsData = {},
        } = {},
      } = action;

      return {
        ...state,
        boardCardsForDropdown: {
          ...state.boardCardsForDropdown,
          ...cardsData,
        },
      };
    }
    case FLUSH_BOARD_CARDS_FOR_DROPDOWN: {
      const {
        payload: {
          boardIds = [],
        } = {},
      } = action;

      const next = flushDropdownBoards(state.boardCardsForDropdown, boardIds);
      if (next === state.boardCardsForDropdown) return state;
      return {
        ...state,
        boardCardsForDropdown: next,
      };
    }
    case GET_CARD_BY_ID: {
      const {
        payload: {
          card = {},
        } = {},
      } = action;
      return {
        ...state,
        selectedCard: card,
      };
    }
    case CLEAR_SELECTED_CARD: {
      const newState = { ...state };
      delete newState.selectedCard;
      newState.cardAttachments = {
        instantiatedFiles: [],
        locationMap: {},
      };
      return newState;
    }
    case CREATE_CARD: {
      const {
        payload: {
          newCard,
        } = {},
      } = action;
      if (!newCard?.id || !newCard?.boardId) return state;
      const { selectedBoard: oldSelectedBoard = {} } = state;
      const {
        boardId, statusId, id, isShadow,
      } = newCard;
      const flushedDropdown = flushDropdownBoards(state.boardCardsForDropdown, [boardId]);
      if (boardId !== oldSelectedBoard?.id) {
        return flushedDropdown === state.boardCardsForDropdown
          ? state
          : { ...state, boardCardsForDropdown: flushedDropdown };
      }
      if (id in (state.fullCards ?? {})) {
        return flushedDropdown === state.boardCardsForDropdown
          ? state
          : { ...state, boardCardsForDropdown: flushedDropdown };
      }
      const newState = {
        ...state,
        boardCardsForDropdown: flushedDropdown,
      };
      newState.fullCards[id] = newCard;
      newState.cardIds = [...newState.cardIds, id];
      const boardSettings = newState.boards[boardId] ?? {};
      if (isShadow) {
        newState.statusToShadowIdMap[statusId] = insertCardIntoCorrectPosition({
          card: newCard,
          boardSettings,
          cardTemplates: newState.cardTemplates,
          fullCards: newState.fullCards,
          destinationCards: newState.statusToShadowIdMap[statusId] ?? [],
        });
      } else {
        newState.statusToCardIdMap[statusId] = insertCardIntoCorrectPosition({
          card: newCard,
          boardSettings,
          cardTemplates: newState.cardTemplates,
          fullCards: newState.fullCards,
          destinationCards: newState.statusToCardIdMap[statusId] ?? [],
        });
      }
      return newState;
    }
    case UPDATE_CARD: {
      const {
        payload: {
          id,
          newData = {},
          addedUsers = [],
          removedUsers = [],
          originalStatusId,
          originalOrderIndex = null,
        } = {},
      } = action;
      const { selectedBoard = {} } = state;
      const { statusId: newStatusId } = newData;

      const matchingStatus = selectedBoard?.statuses?.find?.((status) => status.id === newStatusId);
      if (!matchingStatus) return state;
      const {
        selectedCard: oldSelectedCard,
      } = state;
      const isSelectedCard = oldSelectedCard?.id === id;
      const existingCard = state.fullCards[id];
      if (!existingCard && !isSelectedCard) return state;

      const newState = {
        ...state,
      };

      const statusChanged = newStatusId !== originalStatusId;

      const {
        users = [],
        statusId: stateStatusId,
      } = existingCard ?? {};

      const deleteSet = new Set(removedUsers);
      const fullUserSet = new Set(users.filter((userId) => !deleteSet.has(userId)));
      addedUsers.forEach((userId) => {
        fullUserSet.add(userId);
      });

      const newCard = {
        ...existingCard ?? {},
        ...newData,
        pin: !!newData.pin,
        users: Array.from(fullUserSet),
      };

      newState.fullCards[id] = newCard;

      if (isSelectedCard) {
        newState.selectedCard = newCard;
      }

      const ourKey = newCard.isShadow
        ? 'statusToShadowIdMap'
        : 'statusToCardIdMap';
      const ourMap = newState[ourKey] ?? {};

      const oldCardIds = ourMap[stateStatusId] ?? [];
      ourMap[stateStatusId] = oldCardIds.filter((cardId) => (
        cardId !== id
      ));

      const boardSettings = newState.boards[newState.selectedBoard.id] ?? {};
      newState[ourKey][newStatusId] = insertCardIntoCorrectPosition({
        card: newCard,
        boardSettings,
        cardTemplates: newState.cardTemplates,
        fullCards: newState.fullCards,
        destinationCards: ourMap[newStatusId] ?? [],
      });

      if (!statusChanged) {
        return newState;
      }

      // Only used by LargeBoards. Need to update to force re-order
      newState.cardIds = [...newState.cardIds];

      if (originalOrderIndex === null) return newState;

      // Shift up card beneath card that moved
      oldCardIds.forEach((cardId) => {
        if (!(cardId in newState.fullCards)) return;
        const otherCard = newState.fullCards[cardId];
        if (otherCard.orderIndex < originalOrderIndex) return;
        newState.fullCards[cardId] = {
          ...newState.fullCards[cardId],
          orderIndex: otherCard.orderIndex - 1,
        };
      });
      return newState;
    }
    case DELETE_CARD: {
      const {
        payload: {
          id,
        } = {},
      } = action;
      const deletedBoardId = state.fullCards?.[id]?.boardId;
      const flushedDropdown = flushDropdownBoards(
        state.boardCardsForDropdown,
        [deletedBoardId],
      );
      if (!(id in state.fullCards)) {
        return flushedDropdown === state.boardCardsForDropdown
          ? state
          : { ...state, boardCardsForDropdown: flushedDropdown };
      }
      const {
        statusId,
        orderIndex,
        isShadow,
      } = state.fullCards[id];

      const newState = {
        ...state,
        boardCardsForDropdown: flushedDropdown,
      };
      newState.fullCards = {
        ...newState.fullCards,
      };
      delete newState.fullCards[id];

      newState.cardIds = newState.cardIds.filter((cardId) => cardId !== id);

      const ourKey = isShadow
        ? 'statusToShadowIdMap'
        : 'statusToCardIdMap';
      const ourMap = newState[ourKey] ?? {};

      const newStatusCards = [];
      ourMap[statusId]?.forEach((cardId) => {
        if (cardId === id) return;
        const stateCard = newState.fullCards[cardId];
        if (!stateCard) return;
        // Shift up card beneath card that moved
        newStatusCards.push(cardId);
        if (stateCard.orderIndex < orderIndex) {
          newState.fullCards[id] = {
            ...stateCard,
            orderIndex: stateCard.orderIndex - 1,
          };
        }
      });
      ourMap[statusId] = newStatusCards;

      if (newState?.selectedCard?.id === id) {
        delete newState.selectedCard;
      }

      return newState;
    }
    case MASS_UPLOAD_CARDS: {
      const {
        payload: {
          newCards = [],
        } = {},
      } = action;
      const { selectedBoard: oldSelectedBoard = {} } = state;
      const {
        id: oldBoardId,
      } = oldSelectedBoard;

      const [{ boardId: newBoardId, statusId } = {}] = newCards ?? [];

      const flushedDropdown = flushDropdownBoards(
        state.boardCardsForDropdown,
        [newBoardId],
      );

      if (oldBoardId !== newBoardId) {
        return flushedDropdown === state.boardCardsForDropdown
          ? state
          : { ...state, boardCardsForDropdown: flushedDropdown };
      }

      const newState = {
        ...state,
        boardCardsForDropdown: flushedDropdown,
      };
      const cardIdsToAdd = [];
      newCards.forEach((newCard) => {
        if (newCard.id in state.fullCards) return;
        cardIdsToAdd.push(newCard.id);
        newState.fullCards[newCard.id] = newCard;
      });

      if (cardIdsToAdd.length === 0) return state;

      newState.cardIds = [...newState.cardIds ?? [], ...cardIdsToAdd];
      newState.statusToCardIdMap[statusId] = [
        ...newState.statusToCardIdMap[statusId] ?? [],
        ...cardIdsToAdd,
      ];

      return newState;
    }
    case MOVE_CARD: {
      const {
        payload: {
          id,
          destinationId: destinationIdString,
          destinationIndex,
          sourceId: sourceIdString,
          sourceIndex,
          moverId,
          color,
          boardId,
          shadow,
          status,
        } = {},
      } = action;
      // Flush the destination + source boards from the dropdown cache, since
      // a move may change which board a card belongs to. Done up here so it
      // runs even when the card-mutation logic short-circuits below.
      const previousBoardId = state.fullCards?.[id]?.boardId;
      const flushedDropdown = flushDropdownBoards(
        state.boardCardsForDropdown,
        [boardId, previousBoardId],
      );
      const dropdownChanged = flushedDropdown !== state.boardCardsForDropdown;
      /*
        This gets executed twice when you are the one moving the card
        Once from the action and once from the socket emit.
        Since we are only incrementing and decrementing, the indices become out of sync.
        See: https://projectharbour.atlassian.net/browse/HARBOUR-1835

        We check to see if the moverId === current userId
      */

      if (moverId === Permissions.id || !(id in state.fullCards)) {
        return dropdownChanged
          ? { ...state, boardCardsForDropdown: flushedDropdown }
          : state;
      }
      const destinationId = parseInt(destinationIdString, 10);
      const sourceId = parseInt(sourceIdString, 10);
      const isSameColumn = sourceId === destinationId;
      const isMoveDown = sourceIndex < destinationIndex;
      const {
        selectedBoard: oldSelectedBoard = {},
        selectedCard: oldSelectedCard,
        shortListItemMap: oldShortListItemMap = {},
      } = state;
      const isSelected = oldSelectedCard?.id === id;
      const newState = {
        ...state,
        boardCardsForDropdown: flushedDropdown,
      };

      // Update any shortlist items
      const newShortListItems = {};
      Object.keys(oldShortListItemMap ?? {}).forEach((shortListId) => {
        newShortListItems[shortListId] = oldShortListItemMap[shortListId]?.map((item) => {
          if (item.cardId !== id) return item;
          return {
            ...item,
            status,
          };
        }) ?? [];
      });

      newState.shortListItemMap = newShortListItems;

      if (isSelected && !isSameColumn) {
        newState.selectedCard = {
          ...oldSelectedCard,
          statusId: destinationId,
        };
      }

      const isDifferentBoard = boardId !== oldSelectedBoard?.id;

      if (isDifferentBoard && !shadow) {
        // Remove cards that have changed boards
        // From move board workflow
        newState.fullCards = {
          ...newState.fullCards,
        };
        const { statusId: oldStatusId } = newState.fullCards?.[id] ?? {};
        delete newState.fullCards[id];
        newState.cardIds = newState.cardIds.filter((cardId) => cardId !== id);
        const newStatusCards = [];
        newState.statusToCardIdMap[sourceId]?.forEach((cardId) => {
          if (cardId === id) return;
          const stateCard = newState.fullCards[cardId];
          if (!stateCard) return;
          // Shift up card beneath card that moved
          newStatusCards.push(cardId);
          if (stateCard.orderIndex < sourceIndex) {
            newState.fullCards[id] = {
              ...stateCard,
              orderIndex: stateCard.orderIndex - 1,
            };
          }
        });
        if (oldStatusId) {
          newState.statusToCardIdMap[oldStatusId] = newState
            .statusToCardIdMap[oldStatusId]?.filter((cardId) => (
              cardId !== id
            )) ?? [];
        }

        newState.statusToCardIdMap[sourceId] = newStatusCards;
        return newState;
      }
      const oldCards = newState.statusToCardIdMap[sourceId] ?? [];
      newState.fullCards[id] = {
        ...newState.fullCards[id] ?? {},
        boardId,
        statusId: isDifferentBoard && shadow ? shadow.statusId : destinationId,
        orderIndex: isDifferentBoard && shadow ? oldCards.length : destinationIndex,
        color,
        isShadow: !!(isDifferentBoard && shadow),
      };

      if (!isSameColumn) {
        newState.fullCards[id].lastMoveTimestamp = DateTime.local().toMillis();
      }

      if (isDifferentBoard) {
        // Shadow
        const statusMap = newState.statusToCardIdMap;
        const shadowMap = newState.statusToShadowIdMap;
        statusMap[sourceId] = statusMap[sourceId]?.filter((cardId) => (
          cardId !== id
        ));
        shadowMap[shadow.statusId] = [
          ...shadowMap[shadow.statusId]?.filter((cardId) => cardId !== id) ?? [],
          id,
        ];
        /*
          The card is optimistically moved into the status
          in boards.actions.js

          Since this happens before the backend response, we don't know
          that its a shadow yet and it ends up in the regular card map
        */
        statusMap[shadow.statusId] = statusMap[shadow.statusId]?.filter((cardId) => (
          cardId !== id
        ));
        return newState;
      }
      const sourceCards = newState.statusToCardIdMap[sourceId] ?? [];
      if (!isSameColumn) {
        // Card moved to a different status
        const destinationCards = newState.statusToCardIdMap[destinationId];
        const newSourceCards = [];
        const newDestinationCards = [];
        sourceCards.forEach((cardId) => {
          if (cardId === id) return;
          newSourceCards.push(cardId);
          const fullCard = newState.fullCards[cardId];
          if (!fullCard || fullCard.orderIndex < sourceIndex) return;
          newState.fullCards[cardId] = {
            ...fullCard,
            orderIndex: fullCard.orderIndex - 1,
          };
        });
        destinationCards?.forEach((cardId) => {
          if (cardId === id) return;
          newDestinationCards.push(cardId);
          const fullCard = newState.fullCards[cardId];
          if (!fullCard || fullCard.orderIndex < destinationIndex) return;
          newState.fullCards[cardId] = {
            ...fullCard,
            orderIndex: fullCard.orderIndex + 1,
          };
        });

        const boardSettings = newState.boards[boardId] ?? {};
        const card = newState.fullCards[id];

        newState.statusToCardIdMap[sourceId] = newSourceCards;
        newState.statusToCardIdMap[destinationId] = insertCardIntoCorrectPosition({
          card,
          boardSettings,
          cardTemplates: newState.cardTemplates,
          fullCards: newState.fullCards,
          destinationCards: newDestinationCards,
        });

        // Only used by LargeBoards. Need to update to force re-order
        newState.cardIds = [...newState.cardIds];
        return newState;
      }

      if (isMoveDown) {
        // Card moved to lower spot in same status
        sourceCards.forEach((cardId) => {
          if (cardId === id) return;
          const fullCard = newState.fullCards[cardId];
          if (!fullCard) return;
          const { orderIndex } = fullCard;
          if (orderIndex > sourceIndex && orderIndex <= destinationIndex) {
            newState.fullCards[cardId] = {
              ...fullCard,
              orderIndex: orderIndex - 1,
            };
          }
        });
        // Need to update status state to retrigger ordering re-render
        newState.statusToCardIdMap[sourceId] = [...sourceCards];
        return newState;
      }

      // Card moved to higher spot in same status
      sourceCards.forEach((cardId) => {
        if (cardId === id) return;
        const fullCard = newState.fullCards[cardId];
        if (!fullCard) return;
        const { orderIndex } = fullCard;
        if (orderIndex >= destinationIndex && orderIndex < sourceIndex) {
          newState.fullCards[cardId] = {
            ...fullCard,
            orderIndex: fullCard.orderIndex + 1,
          };
        }
      });
      // Need to update status state to retrigger ordering re-render
      newState.statusToCardIdMap[sourceId] = [...sourceCards];
      return newState;
    }
    case GET_BOARD_CARD_FILES: {
      const {
        payload: {
          id,
          fileMap = {},
        } = {},
      } = action;
      const { cardFileMap: oldFileMap = {} } = state;

      return getAndSetLRUReducer({
        key: 'fileMap',
        stateKey: 'cardFileMap',
        id,
        maxKeysAllowed: MAX_KEYS_ALLOWED,
        stateObject: state,
        oldState: oldFileMap,
        newState: fileMap,
      });
    }
    case GET_CARD_EVENTS: {
      const {
        payload: {
          id,
          events = [],
        } = {},
      } = action;
      const { cardEvents: oldEvents = {} } = state;
      return getAndSetLRUReducer({
        key: 'events',
        stateKey: 'cardEvents',
        id,
        maxKeysAllowed: MAX_KEYS_ALLOWED,
        stateObject: state,
        oldState: oldEvents,
        newState: events,
      });
    }
    case GET_BOARD_CARD_NOTES: {
      const {
        payload: {
          id,
          notes = [],
          fileMap = {},
        } = {},
      } = action;
      const { cardNotesMap: oldNotesMap = {} } = state;
      const newNotesMap = { ...oldNotesMap };
      newNotesMap[id] = notes;
      return {
        ...state,
        cardNotesMap: newNotesMap,
        notesFileMap: fileMap,
      };
    }
    case GET_CARD_ATTACHMENTS: {
      const {
        payload: {
          cardAttachments = {},
        } = {},
      } = action;

      return {
        ...state,
        cardAttachments,
      };
    }
    case ADD_BOARD_CARD_NOTE: {
      const {
        payload = {},
      } = action;
      const { cardId } = payload;
      const { cardNotesMap: stateNotes = {} } = state;
      const existingNotes = { ...stateNotes };
      const {
        [cardId]: existingCardNotes = [],
      } = existingNotes;
      existingNotes[cardId] = existingCardNotes.concat([payload]);
      existingNotes[cardId].sort((a, b) => b.timestamp - a.timestamp);
      return {
        ...state,
        cardNotesMap: existingNotes,
      };
    }
    case GET_CARD_LINKS: {
      const {
        payload: {
          cardLinks = [],
        } = {},
      } = action;
      return {
        ...state,
        cardLinks,
      };
    }
    case GET_CARDS_BY_LINK: {
      const {
        payload: {
          cardUserMap = {},
        } = {},
      } = action;
      return {
        ...state,
        cardUserMap,
      };
    }
    case GET_MY_CARD_TAGS: {
      const {
        payload: {
          myCardTags = [],
          hasRead,
        } = {},
      } = action;
      if (hasRead) {
        return {
          ...state,
          myReadCardTags: myCardTags,
        };
      }
      return {
        ...state,
        myCardTags,
        tagsPerCard: createTagCountMap(myCardTags),
      };
    }
    case MARK_CARD_TAGS_AS_READ: {
      const {
        payload: {
          noteIds = new Set(),
        } = {},
      } = action;
      const { myCardTags: stateCardTags = [] } = state;
      const filteredTags = stateCardTags.filter((tag) => (
        !noteIds.has(tag.cardNoteId)
      ));

      return {
        ...state,
        myCardTags: filteredTags,
        tagsPerCard: createTagCountMap(filteredTags),
      };
    }
    case NEW_TAGS_FROM_SOCKET: {
      const {
        payload: {
          newTags = [],
        } = {},
      } = action;

      const { myCardTags: stateCardTags = [] } = state;
      const existingSet = new Set(stateCardTags.map((tag) => tag.id));
      const fullTags = stateCardTags.concat(
        newTags.filter((tag) => !existingSet.has(tag.id)),
      );

      return {
        ...state,
        myCardTags: fullTags,
        tagsPerCard: createTagCountMap(fullTags),
      };
    }
    case REORDER_CARDS: {
      const {
        payload: {
          type,
          field,
        } = {},
      } = action;

      const newSortType = {
        type,
        field: field ?? null,
      };

      return { ...state, sortType: newSortType };
    }
    case SWITCH_BOARD_VIEW_TYPE: {
      const {
        payload: {
          viewType,
        } = {},
      } = action;
      return { ...state, viewType };
    }
    case SWITCH_BOARD_GANTT_VIEW_TYPE: {
      const {
        payload: {
          ganttViewType,
        } = {},
      } = action;
      return { ...state, ganttViewType };
    }
    case SWITCH_BOARD_GANTT_ZOOM_SCALE: {
      const {
        payload: {
          ganttZoomScale,
        } = {},
      } = action;
      return { ...state, ganttZoomScale };
    }
    case COLLAPSE_ALL_BOARD_SECTIONS: {
      return {
        ...state,
        collapseAllTrigger: { action: 'collapse' },
      };
    }
    case EXPAND_ALL_BOARD_SECTIONS: {
      return {
        ...state,
        collapseAllTrigger: { action: 'expand' },
      };
    }
    case OPEN_CARD_DRAWER: {
      const {
        payload: {
          drawerConfig,
        } = {},
      } = action;
      return { ...state, drawerConfig: { ...drawerConfig, visible: true } };
    }
    case CLOSE_CARD_DRAWER: {
      return { ...state, drawerConfig: {} };
    }
    case OPEN_FORM_MAP_DRAWER: {
      const {
        payload: {
          drawerConfig,
        } = {},
      } = action;
      return { ...state, formMapDrawerConfig: { ...drawerConfig, visible: true } };
    }
    case CLOSE_FORM_MAP_DRAWER: {
      return { ...state, formMapDrawerConfig: {} };
    }
    case OPEN_FORM_DRAWER: {
      const {
        payload: {
          drawerConfig,
        } = {},
      } = action;
      return { ...state, formDrawerConfig: { ...drawerConfig, visible: true } };
    }
    case CLOSE_FORM_DRAWER: {
      return { ...state, formDrawerConfig: {} };
    }
    case OPEN_SHORT_LIST_DRAWER: {
      const {
        payload: {
          drawerConfig,
        } = {},
      } = action;
      return { ...state, shortListDrawerConfig: { ...drawerConfig, visible: true } };
    }
    case CLOSE_SHORT_LIST_DRAWER: {
      return { ...state, shortListDrawerConfig: {} };
    }
    case FIX_BOARD_INDICES: {
      const {
        payload: {
          boardId,
          cards: cardIndexMap = {},
        } = {},
      } = action;
      const { selectedBoard: oldSelectedBoard = {} } = state;
      if (!oldSelectedBoard || oldSelectedBoard.id !== boardId) return state;
      const newState = {
        ...state,
      };
      const statusesToUpdate = new Set();
      Object.keys(cardIndexMap).forEach((cardId) => {
        const existingCard = newState.fullCards[cardId];
        if (!existingCard) return;
        const newOrderIndex = cardIndexMap[cardId];
        statusesToUpdate.add(existingCard.statusId);
        newState.fullCards[cardId] = {
          ...existingCard,
          orderIndex: newOrderIndex,
        };
      });
      Array.from(statusesToUpdate).forEach((statusId) => {
        if (!(statusId in newState.statusToCardIdMap)) return;
        // Need to update status state to retrigger ordering re-render
        newState.statusToCardIdMap[statusId] = [...newState.statusToCardIdMap[statusId] ?? []];
      });

      return newState;
    }
    case GET_CARD_EMAILS: {
      const {
        payload: {
          id,
          emails = [],
        } = {},
      } = action;
      const { cardEmailMap: oldMap = {} } = state;
      const newMap = { ...oldMap };
      newMap[id] = emails;
      return {
        ...state,
        cardEmailMap: newMap,
      };
    }
    case GET_CARD_EMAIL_THREAD: {
      const {
        payload: {
          threadId,
          thread = [],
        } = {},
      } = action;
      const { cardEmailThreads: oldMap = {} } = state;
      const newMap = { ...oldMap };
      newMap[threadId] = thread;
      return {
        ...state,
        cardEmailThreads: newMap,
      };
    }
    case GET_CARD_EMAIL_FILES: {
      const {
        payload: {
          file = {},
          fullFile,
        } = {},
      } = action;
      const { attachmentId } = file;
      if (!attachmentId) return state;
      const { cardEmailFiles: oldMap = {} } = state;
      const newMap = { ...oldMap };
      newMap[attachmentId] = { ...file, jsFileObject: fullFile };
      return {
        ...state,
        cardEmailFiles: newMap,
      };
    }
    case SET_BOARD_TARGET_CARD: {
      const {
        payload: {
          cardId,
          boardId,
          statusId,
        } = {},
      } = action;
      return {
        ...state,
        targetCard: {
          cardId,
          boardId,
          statusId,
        },
      };
    }
    case SET_BOARD_FILTERS: {
      const {
        payload: {
          filters,
        } = {},
      } = action;
      return {
        ...state,
        filters,
      };
    }
    case UPDATE_BOARD_FILTERS: {
      const {
        payload: {
          filters,
        } = {},
      } = action;
      return {
        ...state,
        filters: filters
          ? {
            ...state.filters,
            ...filters,
          }
          : {},
      };
    }
    case GET_BOARD_FILTER_VIEWS: {
      const {
        payload: {
          filterViews,
        } = {},
      } = action;
      const favouriteView = filterViews?.find((view) => view.isFavourite);
      return {
        ...state,
        filterViews,
        filters: favouriteView ? {
          ...favouriteView,
          filterView: favouriteView.id,
        } : state.filters,
      };
    }
    case CREATE_BOARD_FILTER_VIEW:
    case UPDATE_BOARD_FILTER_VIEW:
    case DELETE_BOARD_FILTER_VIEW:
    case FAVOURITE_BOARD_FILTER_VIEW: {
      const {
        payload: {
          filterViews,
        } = {},
      } = action;
      return {
        ...state,
        filterViews,
      };
    }
    case GET_BOARD_FORM_MAPPINGS: {
      const {
        payload: {
          formMappings = {},
        } = {},
      } = action;
      return {
        ...state,
        formMappings,
      };
    }
    case CREATE_BOARD_FORM_MAPPING: {
      const {
        payload: {
          newMapping = {},
        } = {},
      } = action;
      const newMappings = [...state.formMappings];
      newMappings.push(newMapping);
      return {
        ...state,
        formMappings: newMappings,
      };
    }
    case UPDATE_BOARD_FORM_MAPPING: {
      const {
        payload: {
          id,
          newData = {},
        } = {},
      } = action;
      return {
        ...state,
        formMappings: updateArray({ oldArray: state.formMappings, id, newData }),
      };
    }
    case DELETE_BOARD_FORM_MAPPING: {
      const {
        payload: {
          mappingId = {},
        } = {},
      } = action;
      const newMappings = [...state.formMappings];
      return {
        ...state,
        formMappings: Array.from(newMappings).filter((mapping) => mapping.id !== mappingId),
      };
    }
    case GET_BOARD_TEMPLATES: {
      const {
        payload: {
          boardTemplates: newTemplates = [],
        } = {},
      } = action;
      return {
        ...state,
        boardTemplates: newTemplates,
      };
    }
    case CREATE_BOARD_TEMPLATE: {
      const {
        payload: {
          boardTemplate = {},
        } = {},
      } = action;
      const newTemplates = state.boardTemplates.slice();
      const templateIdSet = new Set(state.boardTemplates.map((template) => template.id));
      if (!templateIdSet.has(boardTemplate.id)) newTemplates.push(boardTemplate);
      return {
        ...state,
        boardTemplates: newTemplates,
      };
    }
    case DELETE_BOARD_TEMPLATE: {
      const {
        payload: {
          id: deletedId,
        } = {},
      } = action;
      return {
        ...state,
        boardTemplates: state.boardTemplates.filter((b) => b.id !== deletedId),
      };
    }
    case UPDATE_BOARD_TEMPLATE: {
      const {
        payload: {
          boardTemplate = {},
        } = {},
      } = action;
      return {
        ...state,
        boardTemplates: state.boardTemplates.map(
          (oldTemplate) => (oldTemplate.id === boardTemplate.id
            ? { ...oldTemplate, ...boardTemplate }
            : oldTemplate),
        ),
      };
    }
    case GET_CARD_SNAPSHOTS: {
      const {
        payload: {
          cardId,
          snapshots,
        } = {},
      } = action;

      return getAndSetLRUReducer({
        key: 'snapshots',
        stateKey: 'cardSnapshotMap',
        id: cardId,
        maxKeysAllowed: MAX_KEYS_ALLOWED,
        stateObject: state,
        oldState: state.cardSnapshotMap,
        newState: snapshots,
      });
    }
    case GET_CARD_SNAPSHOT_BY_ID: {
      const {
        payload: {
          cardId,
          snapshot,
        } = {},
      } = action;
      // Should always be in there
      if (!(cardId in state.cardSnapshotMap)) return state;
      const existingSnapshotData = state.cardSnapshotMap[cardId]
      const newSnapshots = existingSnapshotData?.snapshots?.map((s) => (
        s.id === snapshot.id ? snapshot : s
      )) ?? [];
      return {
        ...state,
        cardSnapshotMap: {
          ...state.cardSnapshotMap,
          [cardId]: {
            snapshots: newSnapshots,
            fetchTime: DateTime.local().toMillis(),
          },
        },
      };
    }
    case CLEAR_SNAPSHOT_CARD_DATA: {
      return {
        ...state,
        isFiltered: false,
        filteredSnapshotData: null,
      };
    }
    case SET_SNAPSHOT_CARD_DATA: {
      const {
        payload: {
          filteredData,
        } = {},
      } = action;
      return {
        ...state,
        isFiltered: true,
        filteredSnapshotData: filteredData,
      };
    }
    case GET_ALL_BOARDS_DETAILS: {
      const {
        payload: {
          data = [],
        },
      } = action;
      const newDetails = { ...state.boardDetailsMap };
      data.forEach((board) => {
        newDetails[board.id] = {
          ...newDetails[board.id],
          ...board,
        };
      });
      return {
        ...state,
        boardDetailsMap: newDetails,
      };
    }
    case CLEAR_CARD_SHADOW: {
      const {
        payload: {
          boardId,
          cardId,
        } = {},
      } = action;
      const {
        selectedBoard = {},
      } = state;
      if (boardId !== selectedBoard?.id || !(cardId in state.fullCards)) return state;
      const newState = {
        ...state,
      };
      const { statusId } = newState.fullCards[cardId];
      newState.fullCards = {
        ...newState.fullCards,
      };
      delete newState.fullCards[cardId];

      newState.cardIds = newState.cardIds.filter((id) => id !== cardId);

      const statusCards = newState.statusToShadowIdMap[statusId];
      if (!statusCards) return newState;

      newState.statusToShadowIdMap[statusId] = statusCards.filter((id) => id !== cardId);

      return newState;
    }
    case SET_CARD_DRAGGING: {
      const {
        payload: {
          isDragging,
          sourceId,
        } = {},
      } = action;
      return {
        ...state,
        isDragging,
        dragSourceId: sourceId,
      };
    }
    case GET_BOARD_STATUSES: {
      const {
        payload: {
          boardStatuses = [],
        } = {},
      } = action;
      return {
        ...state,
        boardStatuses,
      };
    }
    case GET_BOARD_COLORS: {
      const {
        payload: {
          boardColors = [],
        } = {},
      } = action;
      return {
        ...state,
        boardColors,
      };
    }
    case SET_FILTERED_CARD_IDS: {
      const {
        payload: { boardId, cardIds },
      } = action;

      const nextMap = { ...state.filteredCardIds };
      nextMap[boardId] = cardIds;

      return { ...state, filteredCardIds: nextMap };
    }
    case GET_SHORT_LIST: {
      const {
        payload: {
          data: {
            list,
            listCategories,
            listItems,
            listPermissions,
          } = {},
        } = {},
      } = action;

      if (!list) return state;

      let hasSeenShortList = false;
      const newShortLists = state.shortLists.map((shortList) => {
        if (shortList.id === list.id) {
          hasSeenShortList = true;
          return {
            ...shortList,
            ...list,
          };
        }
        return shortList;
      });
      if (!hasSeenShortList) newShortLists.push(list);

      return {
        ...state,
        shortLists: newShortLists,
        shortListCategoryMap: {
          ...state.shortListCategoryMap,
          [list.id]: listCategories,
        },
        shortListItemMap: {
          ...state.shortListItemMap,
          [list.id]: listItems,
        },
        shortListPermissionMap: {
          ...state.shortListPermissionMap,
          [list.id]: listPermissions,
        },
      };
    }
    case GET_SHORT_LISTS: {
      const {
        payload: {
          data: {
            lists,
            categories,
            shortListPermissionMap,
          } = {},
        } = {},
      } = action;
      return {
        ...state,
        shortLists: lists,
        shortListCategories: categories,
        shortListPermissionMap: {
          ...state.shortListPermissionMap,
          ...shortListPermissionMap,
        },
      };
    }
    case CREATE_SHORT_LIST: {
      const {
        payload: {
          data: {
            shortList,
            categories,
          } = {},
        } = {},
      } = action;
      return {
        ...state,
        shortLists: [...state.shortLists, shortList],
        shortListCategories: categories,
      };
    }
    case UPDATE_SHORT_LIST: {
      const {
        payload: {
          shortList,
        } = {},
      } = action;
      return {
        ...state,
        shortLists: state.shortLists.map((list) => (list.id === shortList.id ? ({
          ...list,
          ...shortList,
        }) : list)),
      };
    }
    case DELETE_SHORT_LIST: {
      const {
        payload: {
          shortListId,
        } = {},
      } = action;
      return {
        ...state,
        shortLists: state.shortLists.filter((list) => list.id !== shortListId),
      };
    }
    case CREATE_SHORT_LIST_CATEGORY: {
      const {
        payload: {
          data: shortListCategory,
        } = {},
      } = action;
      return {
        ...state,
        shortListCategories: [...state.shortListCategories, shortListCategory],
        shortListCategoryMap: {
          ...state.shortListCategoryMap,
          [shortListCategory.boardShortListId]: [
            ...(state.shortListCategoryMap[shortListCategory.boardShortListId] || []),
            shortListCategory,
          ],
        },
      };
    }
    case UPDATE_SHORT_LIST_CATEGORY: {
      const {
        payload: {
          shortListCategory,
          shortListId,
        } = {},
      } = action;
      const mapFunc = (category) => (category.id === shortListCategory.id ? ({
        ...category,
        ...shortListCategory,
      }) : category);
      return {
        ...state,
        shortListCategories: state.shortListCategories.map(mapFunc),
        shortListCategoryMap: {
          ...state.shortListCategoryMap,
          [shortListId]: (state.shortListCategoryMap[shortListId] || []).map(mapFunc),
        },
      };
    }
    case DELETE_SHORT_LIST_CATEGORY: {
      const {
        payload: {
          shortListCategoryId,
          shortListId,
        } = {},
      } = action;
      const filterFunc = (category) => category.id !== shortListCategoryId;
      return {
        ...state,
        shortListCategories: state.shortListCategories.filter(filterFunc),
        shortListCategoryMap: {
          ...state.shortListCategoryMap,
          [shortListId]: (state.shortListCategoryMap[shortListId] || []).filter(filterFunc),
        },
      };
    }
    case CREATE_SHORT_LIST_ITEM: {
      const {
        payload: {
          data: shortListItem,
        } = {},
      } = action;
      return {
        ...state,
        shortListItemMap: {
          ...state.shortListItemMap,
          [shortListItem.shortListId]: [
            ...(state.shortListItemMap[shortListItem.shortListId] || []),
            shortListItem,
          ],
        },
      };
    }
    case UPDATE_SHORT_LIST_ITEM: {
      const {
        payload: {
          shortListItem,
          shortListId,
        } = {},
      } = action;
      return {
        ...state,
        shortListItemMap: {
          ...state.shortListItemMap,
          [shortListId]: (state.shortListItemMap[shortListId] || []).map(
            (item) => (item.id === shortListItem.id ? ({
              ...item,
              ...shortListItem,
            }) : item),
          ),
        },
      };
    }
    case DELETE_SHORT_LIST_ITEM: {
      const {
        payload: {
          shortListItemId,
          shortListId,
        } = {},
      } = action;

      const updateState = {
        ...state,
        shortListItemMap: {
          ...state.shortListItemMap,
          [shortListId]: (state.shortListItemMap[shortListId] || []).filter(
            (item) => item.id !== shortListItemId,
          ),
        },
      };

      if (state.selectedCard) {
        updateState.selectedCard = {
          ...state.selectedCard,
          shortListItems: state.selectedCard.shortListItems.filter((item) => item.id !== shortListItemId),
        };
      }

      if (!state.fullCards[state.selectedCard?.id] || updateState.selectedCard.shortListItems?.length) {
        return updateState;
      }

      const newFullCards = { ...state.fullCards };
      newFullCards[state.selectedCard?.id] = {
        ...newFullCards[state.selectedCard?.id],
        hasShortListItems: false,
      }

      updateState.fullCards = newFullCards;
      return updateState;
    }
    case ADD_SHORT_LIST_PERMISSION: {
      const {
        payload: {
          data: shortListPermission,
          shortListId,
        } = {},
      } = action;
      return ({
        ...state,
        shortListPermissionMap: {
          ...state.shortListPermissionMap,
          [shortListId]: [...(state.shortListPermissionMap[shortListId] || []), ...shortListPermission],
        },
      })
    }
    case UPDATE_SHORT_LIST_PERMISSION: {
      const {
        payload: {
          shortListPermission,
          shortListId,
          userId,
        } = {},
      } = action;
      return ({
        ...state,
        shortListPermissionMap: {
          ...state.shortListPermissionMap,
          [shortListId]: state.shortListPermissionMap[shortListId].map(
            (permission) => (permission.userId === userId ? ({
              ...permission,
              ...shortListPermission,
            }) : permission),
          ),
        },
      });
    }
    case DELETE_SHORT_LIST_PERMISSION: {
      const {
        payload: {
          userId,
          shortListId,
        } = {},
      } = action;
      return ({
        ...state,
        shortListPermissionMap: {
          ...state.shortListPermissionMap,
          [shortListId]: state.shortListPermissionMap[shortListId].filter((permission) => permission.userId !== userId),
        },
      });
    }
    case ADD_SHORT_LIST_CUSTOM_ITEM: {
      const {
        payload: {
          data: {
            newItem,
            newList,
            newCategory,
          } = {},
        } = {},
      } = action;

      const newState = {
        ...state,
        shortLists: newList ? [...state.shortLists, newList] : state.shortLists,
        shortListCategories: newCategory? [...state.shortListCategories, newCategory] : state.shortListCategories,
      };

      // If the selected card has no short list items, set the hasShortListItems flag to true
      if (state.fullCards[state.selectedCard?.id]) {
        const newFullCards = { ...state.fullCards };
        newFullCards[state.selectedCard?.id] = {
          ...newFullCards[state.selectedCard?.id],
          hasShortListItems: true,
        };
        newState.fullCards = newFullCards;
      }

      if (state.selectedCard) {
        newState.selectedCard = {
          ...state.selectedCard,
          shortListItems: newItem ? [...(state.selectedCard.shortListItems || []), newItem] : state.selectedCard.shortListItems,
        };
      }

      return newState;
    }
    case REORDER_SHORT_LIST_CATEGORIES: {
      const {
        payload: {
          categories,
          shortListId,
        } = {},
      } = action;

      const categoryMap = categories.reduce((acc, category) => {
        acc[category.id] = { orderIndex: category.orderIndex };
        return acc;
      }, {});
      return {
        ...state,
        shortListCategoryMap: {
          ...state.shortListCategoryMap,
          [shortListId]: state.shortListCategoryMap[shortListId]?.map((category) => ({
            ...category,
            ...(categoryMap[category.id] || {}),
          }))?.sort((a, b) => a.orderIndex - b.orderIndex),
        },
      };
    }
    case REORDER_SHORT_LIST_ITEMS: {
      const {
        payload: {
          items,
          shortListId,
        } = {},
      } = action;

      const itemMap = items.reduce((acc, item) => {
        acc[item.id] = { orderIndex: item.orderIndex, boardShortListCategoryId: item.boardShortListCategoryId };
        return acc;
      }, {});

      return {
        ...state,
        shortListItemMap: {
          ...state.shortListItemMap,
          [shortListId]: state.shortListItemMap[shortListId]?.map((item) => ({
            ...item,
            ...(itemMap[item.id] || {}),
          }))?.sort((a, b) => a.orderIndex - b.orderIndex),
        },
      };
    }
    case TOGGLE_CARD_WORK_COMPLETION: {
      const {
        payload: {
          cardId,
          userId,
          completed,
        } = {},
      } = action;
      const { selectedCard: oldSelectedCard = {} } = state;
      const isSelectedCard = oldSelectedCard?.id === cardId;
      const existingCard = state.fullCards[cardId];
      if (!existingCard && !isSelectedCard) return state;

      const newState = {
        ...state,
        fullCards: { ...state.fullCards },
      };

      const {
        workCompletionUsers: oldWorkCompletionUsers = [],
      } = existingCard ?? {};

      const newWorkCompletion = oldWorkCompletionUsers?.map((item) => (item.userId === userId ? ({
        ...item,
        completed,
      }) : item));

      if (existingCard) {
        newState.fullCards[cardId] = {
          ...existingCard,
          workCompletionUsers: newWorkCompletion,
        };
      }

      if (isSelectedCard) {
        newState.selectedCard = {
          ...oldSelectedCard,
          workCompletionUsers: newWorkCompletion,
        };
      }

      return newState;
    }
    case DELETE_USER_FROM_CARD_WORK_COMPLETION: {
      const {
        payload: {
          cardId,
          userId,
        } = {},
      } = action;

      const { selectedCard: oldSelectedCard = {} } = state;
      const isSelectedCard = oldSelectedCard?.id === cardId;
      const existingCard = state.fullCards[cardId];
      if (!existingCard && !isSelectedCard) return state;

      const newState = {
        ...state,
      };

      const {
        workCompletionUsers: oldWorkCompletionUsers = [],
      } = existingCard ?? {};

      const newWorkCompletion = oldWorkCompletionUsers?.filter((item) => item.userId !== userId);

      if (existingCard) {
        newState.fullCards[cardId] = {
          ...existingCard,
          workCompletionUsers: newWorkCompletion,
        };
      }

      if (isSelectedCard) {
        newState.selectedCard = {
          ...oldSelectedCard,
          workCompletionUsers: newWorkCompletion,
        };
      }

      return newState;
    }
    default:
      return state;
  }
};
